/**
 * Vertex AI / Gemini Enterprise Agent Platform Setup Instructions
 *
 * Guides the user through enabling APIs, creating a BigQuery dataset link,
 * and creating a service account with the required roles.
 */

import { Loader2 } from "lucide-react";

interface VertexSetupInstructionsProps {
  isLoading: boolean;
}

export function VertexSetupInstructions({ isLoading }: VertexSetupInstructionsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading setup instructions...
      </div>
    );
  }

  return (
    <div className="space-y-5 pt-3">
      <p className="text-sm text-muted-foreground">
        Follow these steps in your GCP project to set up the Gemini Enterprise Agent Platform
        integration.
      </p>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          1. Go to{" "}
          <strong>GCP Console → APIs & Services → Enable APIs</strong> and enable:
        </p>
        <ul className="ml-6 list-disc text-sm text-muted-foreground space-y-1">
          <li>BigQuery API</li>
          <li>Cloud Trace API</li>
        </ul>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          2. Create a linked BigQuery dataset from the{" "}
          <strong>_Trace</strong> observability bucket
        </p>
        <p className="text-sm text-muted-foreground">
          Run the following command to create a linked dataset for trace spans:
        </p>
        <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
{`gcloud beta observability buckets datasets links create \\
  projects/PROJECT_ID/locations/LOCATION/buckets/_Trace/datasets/Spans/links/LINK_NAME \\
  --dataset=Spans --bucket=_Trace \\
  --location=LOCATION --project=PROJECT_ID`}
        </pre>
        <p className="text-sm text-muted-foreground">
          Use the <code className="text-xs bg-muted px-1 py-0.5 rounded">LINK_NAME</code> as
          the BigQuery dataset name in the form below.
        </p>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          3. Go to{" "}
          <strong>IAM & Admin → Service Accounts → Create Service Account</strong>
        </p>
        <p className="text-sm text-muted-foreground">
          Assign the following roles:
        </p>
        <ul className="ml-6 list-disc text-sm text-muted-foreground space-y-1">
          <li>
            <code className="text-xs bg-muted px-1 py-0.5 rounded">
              BigQuery Data Viewer
            </code>
          </li>
          <li>
            <code className="text-xs bg-muted px-1 py-0.5 rounded">
              BigQuery Job User
            </code>
          </li>
        </ul>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">
          4. Create a <strong>JSON key</strong> for the service account and download it.
        </p>
      </div>

      <div className="space-y-1">
        <p className="text-sm font-medium">
          5. Paste the JSON key contents into the form below along with your project details.
        </p>
        <p className="text-xs text-muted-foreground">
          The key is encrypted at rest and never exposed in API responses.
        </p>
      </div>
    </div>
  );
}
